import { addToCart } from '@hugh/cart-add';

/**
 * <hugh-product>: progressive enhancement for sections/hugh-product.liquid.
 * Without JS the page is a working /cart/add form (variant <select>, pack radios).
 * With JS: option radios resolve the variant, prices and pack totals update, the colour's
 * gallery is re-rendered by the server (Section Rendering API), the add runs over AJAX and
 * dispatches Horizon's CartLinesUpdateEvent so the cart drawer and header count refresh.
 */
class HughProduct extends HTMLElement {
  #abort = new AbortController();
  /** @type {AbortController | undefined} */
  #galleryRequest;
  /** @type {Map<string, Promise<string>>} */
  #galleryCache = new Map();
  /** @type {IntersectionObserver | undefined} */
  #stickyObserver;

  connectedCallback() {
    const signal = this.#abort.signal;
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('form.hugh-buy'));
    this.select = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-variant-select]'));
    this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
    this.variant = this.variants.find((v) => String(v.id) === this.select?.value) || null;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('pointerover', this.#onPrefetch, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.#bindGallery();
    this.#bindTabs(signal);
    this.#watchSticky();
    this.#markAvailability();
    this.#renderTotals();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#galleryRequest?.abort();
    this.#stickyObserver?.disconnect();
  }

  /* ---------- Variant selection ---------- */

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.classList.contains('hugh-colours__tab-input')) this.#openFamily(target.value);
    if (target.dataset.option !== undefined) this.#selectVariant(target.dataset.option === this.#colourIndex());
    if (target.name === 'quantity') this.#setPacks(Number(target.value));
  };

  /** A family tab selects that family's first available colour (owner, 20 Oct 2026). @param {string} family */
  #openFamily(family) {
    const group = [...this.querySelectorAll('.hugh-colours__group')].find((g) => g instanceof HTMLElement && g.dataset.family === family);
    const inputs = [...(group?.querySelectorAll('.hugh-colours__input') ?? [])].filter((i) => i instanceof HTMLInputElement);
    const first = inputs.find((i) => !i.hasAttribute('data-unavailable')) ?? inputs[0];
    if (!first || first.checked) return;
    first.checked = true;
    this.#selectVariant(true);
  }

  /** @returns {string[]} Selected value per option position. */
  #selectedOptions() {
    const selected = [];
    for (const input of this.querySelectorAll('input[data-option]:checked')) {
      if (input instanceof HTMLInputElement) selected[Number(input.dataset.option)] = input.value;
    }
    return selected;
  }

  /** @returns {string | undefined} Option index of the colour picker. */
  #colourIndex() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('.hugh-colours__input'))?.dataset.option;
  }

  /** @param {boolean} colourChanged */
  #selectVariant(colourChanged) {
    const options = this.#selectedOptions();
    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i])) || null;
    this.variant = variant;

    const colourIndex = this.#colourIndex();
    const name = this.querySelector('[data-colour-name]');
    if (name && colourIndex !== undefined) name.textContent = options[Number(colourIndex)] || '';

    if (variant && this.select) this.select.value = String(variant.id);
    this.#markAvailability();
    this.#renderTotals();
    this.#renderSticky();

    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    history.replaceState(history.state, '', url);
    if (colourChanged) this.#loadGallery(variant.id);
  }

  /** Strikes values that have no available variant with the other current selections. */
  #markAvailability() {
    const selected = this.#selectedOptions();
    for (const input of this.querySelectorAll('input[data-option]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const index = Number(input.dataset.option);
      const available = this.variants.some(
        (v) => v.available && v.options[index] === input.value && v.options.every((value, i) => i === index || value === selected[i])
      );
      input.toggleAttribute('data-unavailable', !available);
    }
  }

  /* ---------- Prices ---------- */

  /** @param {number} cents */
  #format(cents) {
    return this.money.format(cents / 100);
  }

  #renderTotals() {
    const variant = this.variant;
    const price = variant?.price ?? 0;
    const pairs = Number(this.dataset.pairs) || 0;

    const priceEl = this.querySelector('[data-price]');
    if (priceEl && variant) priceEl.textContent = this.#format(price);
    const compare = this.querySelector('[data-compare]');
    if (compare) compare.toggleAttribute('hidden', !variant || variant.compare <= price);
    const perPair = this.querySelector('[data-per-pair]');
    if (perPair && pairs) perPair.textContent = this.#format(Math.round(price / pairs));

    const step = this.querySelector('[data-pack]');
    const qty = this.#clampPacks(Number(this.#packInput()?.value));
    // The store's discount: the best tier the pack count reaches; per-pack discount rounded down.
    const reached = this.#tiers().filter(([min]) => qty >= min);
    const [, pct] = reached.at(-1) ?? [1, 0];
    const unit = price - Math.floor((price * pct) / 100);
    const selectedTotal = unit * qty;
    // Beside "Packs": the next saving and how many packs reach it; at the top tier, the saving reached.
    const next = this.#tiers().find(([min, p]) => min > qty && p > pct);
    const nextEl = this.querySelector('[data-pack-next]');
    if (nextEl instanceof HTMLElement && step instanceof HTMLElement) {
      nextEl.toggleAttribute('hidden', !next);
      if (next) {
        const text = (step.dataset.next || '').replace('[n]', String(next[0] - qty)).replace('[p]', String(next[1]));
        nextEl.dataset.setQty = String(next[0]);
        if (nextEl.textContent !== text) {
          nextEl.textContent = text;
          // Replays the pop and sweep so a new offer catches the eye.
          nextEl.classList.remove('is-new');
          void nextEl.offsetWidth;
          nextEl.classList.add('is-new');
        }
      }
    }
    const saveEl = this.querySelector('[data-pack-save]');
    const savedText = this.querySelector('[data-pack-saved]');
    if (saveEl && savedText && step instanceof HTMLElement) {
      savedText.textContent = (step.dataset.saved || '').replace('[p]', String(pct));
      saveEl.toggleAttribute('hidden', Boolean(next) || pct === 0);
    }
    // Colour step: discount tiers reached, or "top" at the best one (CSS walks red → green).
    const meta = this.querySelector('.hugh-pack__meta');
    if (meta instanceof HTMLElement) {
      meta.dataset.stage = !next && pct > 0 ? 'top' : String(reached.filter(([, p]) => p > 0).length);
    }
    for (const button of this.querySelectorAll('[data-step-qty]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = Number(button.dataset.stepQty) < 0 ? qty <= 1 : qty >= this.#clampPacks(Infinity);
    }

    const available = Boolean(variant?.available);
    for (const button of this.querySelectorAll('[data-add]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = !available;
      const total = button.querySelector('[data-add-total]');
      if (total) {
        total.textContent = `· ${this.#format(selectedTotal)}`;
        total.toggleAttribute('hidden', !available);
      }
    }
    const label = this.querySelector('[data-add-label]');
    if (label) label.textContent = this.#t(available ? 'add' : variant ? 'soldOut' : 'unavailable');
    this.querySelector('.hugh-buy__express')?.toggleAttribute('hidden', !available);
  }

  /** @param {'add' | 'soldOut' | 'unavailable' | 'added' | 'error'} key */
  #t(key) {
    this.i18n ??= JSON.parse(this.dataset.i18n || '{}');
    return this.i18n[key] || '';
  }

  /* ---------- Gallery ---------- */

  #bindGallery() {
    const track = this.querySelector('[data-track]');
    if (!track) return;
    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.#syncGallery());
      },
      { signal: this.#abort.signal, passive: true }
    );
  }

  /** @returns {number} Index of the slide in view. */
  #slideIndex() {
    const track = this.querySelector('[data-track]');
    if (!(track instanceof HTMLElement) || !track.clientWidth) return 0;
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  /** @param {number} index */
  #goTo(index) {
    const track = this.querySelector('[data-track]');
    if (!(track instanceof HTMLElement)) return;
    const count = track.children.length;
    const next = (index + count) % count;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollTo({ left: next * track.clientWidth * rtl, behavior: this.#motion() });
  }

  #syncGallery() {
    const index = this.#slideIndex();
    const counter = this.querySelector('[data-index]');
    if (counter) counter.textContent = String(index + 1);
    this.querySelectorAll('[data-go]').forEach((thumb, i) => {
      thumb.setAttribute('aria-current', String(i === index));
    });
  }

  /** @returns {ScrollBehavior} */
  #motion() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** @param {number | string} variantId */
  #galleryUrl(variantId) {
    const url = new URL(this.dataset.url || window.location.pathname, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    url.searchParams.set('section_id', this.dataset.section || '');
    return url.toString();
  }

  /** @param {number | string} variantId */
  #fetchGallery(variantId, signal) {
    const key = String(variantId);
    if (!this.#galleryCache.has(key)) {
      const request = fetch(this.#galleryUrl(variantId), { signal }).then((r) => {
        if (!r.ok) throw new Error(`Gallery ${r.status}`);
        return r.text();
      });
      request.catch(() => this.#galleryCache.delete(key));
      this.#galleryCache.set(key, request);
    }
    return /** @type {Promise<string>} */ (this.#galleryCache.get(key));
  }

  /** Swaps in the selected colour's images, rendered by the server. @param {number} variantId */
  async #loadGallery(variantId) {
    this.#galleryRequest?.abort();
    this.#galleryRequest = new AbortController();
    const current = this.querySelector('[data-hugh-gallery]');
    current?.setAttribute('aria-busy', 'true');
    try {
      const html = await this.#fetchGallery(variantId, this.#galleryRequest.signal);
      if (this.variant?.id !== variantId) return;
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hugh-gallery]');
      if (next && current) {
        current.replaceWith(document.importNode(next, true));
        this.#bindGallery();
      }
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') current?.removeAttribute('aria-busy');
    }
  }

  /** Prefetches a colour's gallery when the pointer reaches its swatch. @param {PointerEvent} event */
  #onPrefetch = (event) => {
    const label = event.target instanceof Element ? event.target.closest('.hugh-colours__swatch') : null;
    const input = label?.previousElementSibling;
    if (!(input instanceof HTMLInputElement) || input.checked) return;
    const index = Number(input.dataset.option);
    const options = this.#selectedOptions();
    options[index] = input.value;
    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
    if (variant) this.#fetchGallery(variant.id).catch(() => {});
  };

  /* ---------- Clicks: gallery, zoom, dialogs, finder ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('button');
    if (!button || !this.contains(button)) return;

    if (button.dataset.step) return this.#goTo(this.#slideIndex() + Number(button.dataset.step));
    if (button.dataset.tabsMove) {
      const tabs = this.querySelector('[data-tabs]');
      return tabs?.scrollBy({ left: Number(button.dataset.tabsMove) * tabs.clientWidth * 0.7, behavior: 'smooth' });
    }
    if (button.dataset.stepQty) return this.#setPacks(this.#clampPacks(Number(this.#packInput()?.value)) + Number(button.dataset.stepQty));
    if (button.dataset.setQty) return this.#setPacks(Number(button.dataset.setQty));
    if (button.dataset.go) return this.#goTo(Number(button.dataset.go));
    if (button.dataset.zoom) return this.#openZoom(Number(button.dataset.zoom));
    if (button.dataset.open) {
      const dialog = this.querySelector(`#${CSS.escape(button.dataset.open)}`);
      if (dialog instanceof HTMLDialogElement) dialog.showModal();
      if (button.hasAttribute('data-finder')) dialog?.querySelector('select')?.focus();
      return;
    }
    if (button.hasAttribute('data-close')) return button.closest('dialog')?.close();
    if (button.dataset.choose) return this.#chooseSize(button);
  };

  /* ---------- Colour family tabs ---------- */

  /** Shows the tab arrows only when the row can scroll that way. @param {AbortSignal} signal */
  #bindTabs(signal) {
    const wrap = this.querySelector('[data-tabs-wrap]');
    const tabs = this.querySelector('[data-tabs]');
    if (!(wrap instanceof HTMLElement) || !(tabs instanceof HTMLElement)) return;
    const update = () => {
      const start = tabs.scrollLeft <= 2;
      const end = tabs.scrollLeft + tabs.clientWidth >= tabs.scrollWidth - 2;
      wrap.toggleAttribute('data-scrolled', !start);
      wrap.toggleAttribute('data-end', end);
      wrap.querySelector('[data-tabs-move="-1"]')?.toggleAttribute('hidden', start);
      wrap.querySelector('[data-tabs-move="1"]')?.toggleAttribute('hidden', end);
    };
    tabs.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('resize', update, { signal });
    // Keep the chosen family in view when it is picked with the keyboard.
    tabs.addEventListener('change', (event) => {
      const label = event.target instanceof HTMLInputElement ? event.target.nextElementSibling : null;
      label?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }, { signal });
    update();
  }

  /* ---------- Packs ---------- */

  /** @returns {HTMLInputElement | null} The pack count field, if the product sells more than one pack. */
  #packInput() {
    const input = this.querySelector('[data-pack] input[name="quantity"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {Array<[number, number]>} Pack tiers [minimum packs, percent], ascending. */
  #tiers() {
    if (this.#tierList) return this.#tierList;
    const step = this.querySelector('[data-pack]');
    try {
      const list = JSON.parse(step instanceof HTMLElement ? step.dataset.tiers || '[]' : '[]');
      this.#tierList = Array.isArray(list) ? list.filter((t) => Array.isArray(t) && t.length === 2).map(([a, b]) => [Number(a), Number(b)]).sort((a, b) => a[0] - b[0]) : [];
    } catch {
      this.#tierList = [];
    }
    return this.#tierList;
  }

  /** @type {Array<[number, number]> | null} */
  #tierList = null;

  /** Validates a pack count before it reaches the page or the Cart API: whole number, 1…max. @param {number} value */
  #clampPacks(value) {
    const max = Number(this.#packInput()?.max) || 1;
    return Math.min(max, Math.max(1, Math.floor(value) || 1));
  }

  /** @param {number} value */
  #setPacks(value) {
    const input = this.#packInput();
    if (!input) return;
    input.value = String(this.#clampPacks(value));
    this.#renderTotals();
  }

  /** @param {number} index */
  #openZoom(index) {
    const dialog = this.querySelector('[data-zoom-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    dialog.showModal();
    dialog.querySelectorAll('[data-zoom-item]')[index]?.scrollIntoView({ block: 'start' });
  }

  /* ---------- Size finder ---------- */

  /**
   * Suggests a size from the shopper's shoe size. Shoe and age sizes use each button's data-min/max;
   * letter sizes (Medium, Large…) use the women's or men's range the shopper picked; a sock sold in one
   * size (data-one-min/max on the form) only says whether the shoe size fits.
   * @param {HTMLFormElement} form
   */
  #findSize(form) {
    const data = new FormData(form);
    const value = Number(data.get('value'));
    const swelling = data.has('swelling');
    const who = String(data.get('who') || '');
    const result = form.querySelector('[data-finder-result]');
    if (!result || !value) return;
    const strings = JSON.parse(form.dataset.i18n || '{}');
    const number = (/** @type {number} */ n) => String(n).replace(/\.0$/, '');

    const oneMin = Number(form.dataset.oneMin);
    const oneMax = Number(form.dataset.oneMax);
    if (oneMin && oneMax) {
      const fitsOne = value >= oneMin && value <= oneMax;
      const line = document.createElement('p');
      line.textContent = String((fitsOne ? strings.yes : strings.no) || '')
        .replace('{{ size }}', number(value))
        .replace('{{ range }}', `${number(oneMin)}–${number(oneMax)}`);
      result.replaceChildren(line);
      return;
    }

    const suffix = who === 'women' ? 'Women' : who === 'men' ? 'Men' : '';
    const sizes = [...this.querySelectorAll(`input[data-option="${form.dataset.option}"][data-kind]`)]
      .filter((input) => input instanceof HTMLInputElement)
      .map((input) => ({ input, min: Number(input.dataset[`min${suffix}`]), max: Number(input.dataset[`max${suffix}`]) }))
      .filter((size) => size.min > 0 && size.max > 0)
      .sort((a, b) => a.min - b.min);
    if (!sizes.length) return;

    const fits = sizes.filter((s) => value >= s.min && value <= s.max);
    let pick = fits.at(-1);
    let reason = 'fits';
    if (fits.length > 1) reason = 'overlap';
    if (!pick) {
      pick = sizes.find((s) => s.min > value) || sizes.at(-1);
      reason = value < sizes[0].min ? 'smallest' : value > sizes.at(-1).max ? 'largest' : 'between';
    }
    const larger = sizes[sizes.indexOf(pick) + 1];
    if (swelling && reason === 'fits' && larger && value >= pick.max - 1) {
      pick = larger;
      reason = 'swelling';
    }

    const label = pick.input.nextElementSibling?.querySelector('.hugh-opt__main')?.textContent || pick.input.value;
    result.replaceChildren();
    const lead = document.createElement('p');
    const strong = document.createElement('strong');
    strong.textContent = (strings.result || '').replace('{{ size }}', label);
    lead.append(strong, ' ', strings[reason] || '');
    const choose = document.createElement('button');
    choose.type = 'button';
    choose.className = 'hugh-btn';
    choose.dataset.choose = pick.input.id;
    choose.textContent = (strings.choose || '').replace('{{ size }}', label);
    result.append(lead, choose);
  }

  /** @param {HTMLButtonElement} button */
  #chooseSize(button) {
    const input = this.querySelector(`#${CSS.escape(button.dataset.choose || '')}`);
    if (!(input instanceof HTMLInputElement)) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    button.closest('dialog')?.close();
    input.focus();
  }

  /* ---------- Add to cart ---------- */

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.hasAttribute('data-finder-form')) {
      event.preventDefault();
      return this.#findSize(form);
    }
    if (form !== this.form) return;
    event.preventDefault();
    if (!this.variant?.available) return;

    const buttons = [...this.querySelectorAll('[data-add]')];
    if (buttons.some((b) => b.getAttribute('aria-busy') === 'true')) return;
    const body = new FormData(form);
    const quantity = this.#packInput() ? this.#clampPacks(Number(body.get('quantity'))) : Math.max(1, Math.floor(Number(body.get('quantity')) || 1));
    body.set('quantity', String(quantity));
    body.set('id', String(this.variant.id));

    const status = this.querySelector('[data-status]');
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    try {
      const error = await addToCart(this, body, [{ merchandiseId: String(this.variant.id), quantity }], 'product');
      if (status) status.textContent = error ? (error === 'error' ? this.#t('error') : error) : '';
    } catch {
      if (status) status.textContent = this.#t('error');
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  };

  /* ---------- Sticky bar ---------- */

  #watchSticky() {
    const sticky = this.querySelector('[data-sticky]');
    const main = this.querySelector('[data-buy-main]');
    if (!(sticky instanceof HTMLElement) || !main) return;
    sticky.hidden = false;
    this.#stickyObserver = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.toggleAttribute('data-visible', show);
      sticky.inert = !show;
    });
    this.#stickyObserver.observe(main);
  }

  #renderSticky() {
    const variant = this.variant;
    const text = this.querySelector('[data-sticky-variant]');
    // Same short form as the server: "Dark Grey · 5-8" (no "Shoe Size:" label).
    if (text) text.textContent = (variant?.options || []).map((o) => o.replace(/^shoe size:\s*/i, '')).join(' · ');
    const image = this.querySelector('[data-sticky-img]');
    if (image instanceof HTMLImageElement && variant?.image) {
      image.src = variant.image;
      image.removeAttribute('srcset');
    }
  }
}

if (!customElements.get('hugh-product')) customElements.define('hugh-product', HughProduct);
